import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Toaster, toast } from "sonner";
import {
  ArrowLeft,
  Sparkles,
  Clock,
  Flame,
  Heart,
  TrendingUp,
  Target,
  Trash2,
  Calendar,
  X,
} from "lucide-react";
import { BrandMark } from "@/components/BrandMark";
import GoogleAuthButton from "@/components/GoogleAuthButton";
import LandingBottomNav from "@/components/LandingBottomNav";
import CartIQAsk from "@/components/CartIQAsk";
import { useAuth } from "@/contexts/AuthContext";
import { joinIntelligenceWaitlist } from "@/lib/api";

/**
 * Intelligence tab — product preview + waitlist capture.
 *
 * Stand-alone scrollable page showing what Super Intelligence will unlock.
 * Has 5 layers of insight cards, a Health Intelligence highlight section,
 * and a waitlist form at the bottom that POSTs to /api/intelligence/waitlist.
 */

// ─── Insight card (compact, left-border accent) ──────────────────────────────
const InsightCard = ({ color, title, quote, sub }) => (
  <div
    className="bg-white text-[#0A0A0F] border border-[#E5E1DC] shadow-sm"
    style={{ borderLeft: `3px solid ${color}` }}
    data-testid="intel-insight-card"
  >
    <div className="px-3.5 py-3 md:px-4 md:py-3.5">
      <div
        className="text-[13px] font-medium leading-snug"
        data-testid="intel-insight-title"
      >
        {title}
      </div>
      <div className="mt-1 text-[12px] text-[#555] italic leading-[1.6]">
        {quote}
      </div>
      {sub && (
        <div
          className="mt-1.5 text-[10px] font-medium"
          style={{ color }}
        >
          {sub}
        </div>
      )}
    </div>
  </div>
);

// ─── Section wrapper for each "layer" ────────────────────────────────────────
const LayerSection = ({
  id,
  icon: Icon,
  accent,
  layerLabel,
  title,
  subtitle,
  badge,
  badgeTone = "default",
  children,
}) => {
  const badgeStyle = {
    default: { bg: `${accent}18`, color: accent, border: `${accent}40` },
    saffron: { bg: "#FF6B00", color: "#FFFFFF", border: "#FF6B00" },
    green: { bg: "#27500A", color: "#FFFFFF", border: "#27500A" },
  }[badgeTone] || { bg: `${accent}18`, color: accent, border: `${accent}40` };

  return (
    <section
      id={id}
      className="max-w-5xl mx-auto px-5 md:px-8 mt-10 md:mt-14"
      data-testid={`intel-layer-${id}`}
    >
      <div
        className="flex items-center gap-3 pb-3 border-b"
        style={{ borderColor: `${accent}33` }}
      >
        <span
          className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
          style={{ background: accent }}
          aria-hidden="true"
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div
              className="text-[10px] uppercase tracking-[0.22em] font-bold"
              style={{ color: accent }}
            >
              {layerLabel}
            </div>
            <span
              className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] uppercase tracking-[0.14em] font-bold border"
              style={{
                background: badgeStyle.bg,
                color: badgeStyle.color,
                borderColor: badgeStyle.border,
              }}
            >
              {badge}
            </span>
          </div>
          <div className="mt-1 font-display uppercase text-lg md:text-2xl tracking-tight flex items-center gap-2">
            {Icon && (
              <Icon
                className="w-4 h-4 md:w-5 md:h-5"
                style={{ color: accent }}
                aria-hidden="true"
              />
            )}
            {title}
          </div>
          {subtitle && (
            <div className="mt-1 text-[12px] md:text-[13px] text-[#888]">
              {subtitle}
            </div>
          )}
        </div>
      </div>
      <div className="mt-5">{children}</div>
    </section>
  );
};

// ─── Waitlist modal ──────────────────────────────────────────────────────────
const WaitlistModal = ({ open, onClose }) => {
  const { user } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    const n = name.trim();
    const em = email.trim();
    if (!n) {
      toast.error("Please enter your name.");
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(em)) {
      toast.error("Please enter a valid email.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await joinIntelligenceWaitlist({
        name: n,
        email: em,
        source: "intelligence_tab",
      });
      setDone(true);
      toast.success(res.message || "You're on the list.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not join waitlist. Try again.";
      toast.error(typeof msg === "string" ? msg : "Could not join waitlist.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
      role="dialog"
      aria-modal="true"
      data-testid="waitlist-modal"
      onClick={onClose}
    >
      <div
        className="bg-[#11111A] border border-[#2A2A35] w-full max-w-md p-6 md:p-8 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 text-[#666] hover:text-[#F0EDE8]"
          data-testid="waitlist-close-btn"
        >
          <X className="w-5 h-5" />
        </button>

        {done ? (
          <div className="text-center py-4">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#1D9E75]/15 border border-[#1D9E75]/50 mb-4">
              <Sparkles className="w-7 h-7 text-[#1D9E75]" />
            </div>
            <div className="font-display uppercase text-2xl tracking-tight">
              You&rsquo;re on the list.
            </div>
            <p className="mt-3 text-sm text-[#888]">
              We&rsquo;ll email you the moment Super Intelligence ships.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="mt-6 btn-saffron px-6 py-2.5"
              data-testid="waitlist-done-btn"
            >
              Got it
            </button>
          </div>
        ) : (
          <form onSubmit={submit}>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#FF6B00] font-bold">
              Waitlist
            </div>
            <h3
              className="font-display uppercase text-2xl mt-2 tracking-tight"
              data-testid="waitlist-modal-heading"
            >
              Join the Super Intelligence waitlist
            </h3>
            <p className="mt-2 text-sm text-[#888]">
              We&rsquo;ll email you the moment it&rsquo;s ready. No spam, no upsell.
            </p>

            <div className="mt-5 space-y-4">
              <div>
                <label className="text-[11px] uppercase tracking-[0.22em] text-[#888] font-bold block mb-1.5">
                  Your name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ritu"
                  className="cartiq-input w-full"
                  required
                  maxLength={120}
                  data-testid="waitlist-name-input"
                />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.22em] text-[#888] font-bold block mb-1.5">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="cartiq-input w-full"
                  required
                  maxLength={200}
                  data-testid="waitlist-email-input"
                />
              </div>
            </div>

            <button
              type="submit"
              className="mt-6 btn-saffron w-full h-12 flex items-center justify-center gap-2"
              disabled={submitting}
              data-testid="waitlist-submit-btn"
            >
              {submitting ? "Joining…" : "Join the waitlist"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

// ─── Main page ───────────────────────────────────────────────────────────────
export default function IntelligenceTab() {
  const [waitlistOpen, setWaitlistOpen] = useState(false);

  return (
    <div
      className="cartiq-wellness min-h-screen bg-[#F8FAFC] text-[#334155] relative pb-20 md:pb-10"
      data-testid="intelligence-page"
    >
      <Toaster position="top-center" richColors theme="light" />

      {/* Header */}
      <header className="border-b border-[#2A2A35] bg-[#0A0A0F]/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2">
          <BrandMark />
          <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
            <Link
              to="/"
              className="text-[10px] sm:text-xs uppercase tracking-[0.14em] sm:tracking-[0.18em] font-bold px-2 sm:px-3 py-1.5 sm:py-2 border border-[#2A2A35] text-[#888] hover:text-[#F0EDE8] hover:border-[#F0EDE8] transition-colors whitespace-nowrap inline-flex items-center gap-1.5"
              data-testid="intel-back-link"
            >
              <ArrowLeft className="w-3 h-3" />
              <span className="hidden sm:inline">Home</span>
              <span className="sm:hidden">Back</span>
            </Link>
            <GoogleAuthButton target="/intelligence" />
            <button
              type="button"
              onClick={() => setWaitlistOpen(true)}
              className="btn-saffron text-xs sm:text-sm px-2.5 sm:px-4 py-2 whitespace-nowrap"
              data-testid="intel-header-cta"
            >
              <span className="sm:hidden">Join →</span>
              <span className="hidden sm:inline">Join waitlist →</span>
            </button>
          </div>
        </div>
      </header>

      {/* HERO ───────────────────────────────────────────────────────────── */}
      <section
        className="max-w-4xl mx-auto px-6 pt-14 md:pt-20 pb-8 text-center"
        data-testid="intel-hero"
      >
        <div
          className="inline-flex items-center gap-2 px-3 py-1 border border-[#FF6B00] text-[#FF6B00] text-[11px] uppercase tracking-[0.22em] font-bold"
          data-testid="intel-hero-badge"
        >
          <Sparkles className="w-3 h-3" />
          Super intelligence — coming soon
        </div>

        <h1
          className="font-display uppercase mt-6 text-[clamp(34px,6vw,64px)] leading-[1.05] tracking-tight"
          data-testid="intel-hero-headline"
        >
          CartIQ knows your food life
          <br />
          <span className="text-[#FF6B00]">better than you do.</span>
        </h1>

        <p
          className="mt-5 text-base md:text-lg text-[#888] max-w-2xl mx-auto leading-[1.6]"
          data-testid="intel-hero-subheadline"
        >
          Six layers of intelligence built on your real purchase data. Not averages.
          Not estimates. Your actual food life — decoded.
        </p>

        <div className="mt-6 flex justify-center">
          <span
            className="inline-flex items-center gap-2 px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] font-bold"
            style={{ background: "#FF6B00", color: "#FFFFFF" }}
            data-testid="intel-availability-pill"
          >
            Available in Pro and above · Join the waitlist
          </span>
        </div>
      </section>

      {/* LAYER 1 — Behavioural ───────────────────────────────────────────── */}
      <LayerSection
        id="behavioural"
        icon={Clock}
        accent="#FF6B00"
        layerLabel="Layer 1 — Behavioural patterns"
        title="How you actually eat"
        subtitle="Every order you place tells a story. CartIQ reads it."
        badge="Live now"
        badgeTone="saffron"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <InsightCard
            color="#FF6B00"
            title="Friday craving pattern"
            quote="You order comfort food 73% of Friday evenings after 8pm — likely after a long work week."
          />
          <InsightCard
            color="#FF6B00"
            title="Mid-week ordering spike"
            quote="3 Blinkit orders every Wednesday vs 1 on other days. One planned shop saves ₹800/month."
          />
          <InsightCard
            color="#FF6B00"
            title="Late night emotional ordering"
            quote="38% of your Zomato orders happen between 10pm and 1am — mostly high-calorie comfort food."
          />
        </div>
      </LayerSection>

      {/* HEALTH INTELLIGENCE ─────────────────────────────────────────────── */}
      <section
        className="max-w-5xl mx-auto px-5 md:px-8 mt-12 md:mt-16"
        data-testid="intel-layer-health"
      >
        <div className="border" style={{ borderColor: "#639922" }}>
          {/* Section header */}
          <div
            className="px-4 md:px-6 py-4 md:py-5 flex items-start gap-3 flex-wrap"
            style={{ background: "#EAF3DE", color: "#27500A" }}
          >
            <Heart
              className="w-5 h-5 mt-0.5 flex-shrink-0"
              style={{ color: "#27500A" }}
              aria-hidden="true"
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold">
                  Layer 1.5 — Health intelligence
                </div>
                <span
                  className="inline-flex items-center px-2 py-0.5 text-[9px] uppercase tracking-[0.14em] font-bold"
                  style={{ background: "#27500A", color: "#FFFFFF" }}
                  data-testid="intel-health-badge"
                >
                  Coming soon · High demand
                </span>
              </div>
              <div
                className="mt-1 font-display uppercase text-lg md:text-2xl tracking-tight"
                data-testid="intel-health-heading"
              >
                The food health mirror
              </div>
              <div className="mt-1 text-[12px] md:text-[13px]" style={{ color: "#27500A" }}>
                We don&rsquo;t sell supplements. We just show you the pattern.
              </div>
            </div>
          </div>

          {/* 3 metric cards */}
          <div className="px-4 md:px-6 pt-5">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {[
                { v: "54", sub: "Your household nutrition score", color: "#27500A" },
                { v: "40%", sub: "Processed food in your basket", color: "#BA7517" },
                { v: "28%", sub: "Fresh produce — down from 45%", color: "#E24B4A" },
              ].map((m, i) => (
                <div
                  key={i}
                  className="p-4 md:p-5 border"
                  style={{ background: "#EAF3DE", borderColor: "#63992230" }}
                  data-testid={`intel-health-metric-${i}`}
                >
                  <div
                    className="font-display font-mono-num text-3xl md:text-4xl leading-none"
                    style={{ color: m.color }}
                  >
                    {m.v}
                  </div>
                  <div
                    className="mt-2 text-[11px] md:text-[12px] leading-snug"
                    style={{ color: "#27500A" }}
                  >
                    {m.sub}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 4 insight cards */}
          <div className="px-4 md:px-6 py-5 md:py-6 space-y-2">
            <InsightCard
              color="#639922"
              title="Household nutrition score"
              quote="Your family's nutrition score dropped from 68 to 54 over 3 months. Fresh produce buying fell 40% while convenience food rose 28%. CartIQ suggests adding 2 more vegetable categories to your weekly list."
              sub="Based on 3 months of purchase data"
            />
            <InsightCard
              color="#639922"
              title="Junk food frequency alert"
              quote="You have ordered pizza, burgers or fried snacks 18 times this month — every 1.7 days on average. Your household junk food spend is ₹4,200 this month — 29% of total food budget."
              sub="Not judging — just showing you the picture"
            />
            <InsightCard
              color="#639922"
              title="Protein and dairy tracking"
              quote="Your protein buying (eggs, paneer, lentils, chicken) accounts for only 14% of your grocery spend — below recommended levels for a family of 4. Your dairy is strong at 22%."
              sub="Weekly list updated with protein suggestions"
            />
            <InsightCard
              color="#639922"
              title="Sugar and snack pattern"
              quote="Your household buys 8-10 sugary snack items every week — biscuits, chocolates, soft drinks, chips. Switching 3 items to healthier alternatives saves ₹480/month too."
              sub="Saves money and improves your score"
            />
          </div>
        </div>
      </section>

      {/* LAYER 2 — Predictive ────────────────────────────────────────────── */}
      <LayerSection
        id="predictive"
        icon={TrendingUp}
        accent="#1D9E75"
        layerLabel="Layer 2 — Predictive intelligence"
        title="What&rsquo;s about to happen"
        subtitle="We look ahead so you don&rsquo;t get surprised on the 24th."
        badge="Coming soon"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <InsightCard
            color="#1D9E75"
            title="Budget overshoot prediction"
            quote="You will hit ₹14,200 by month end at current pace — 6 days before salary. CartIQ recommends pausing Zomato this week."
          />
          <InsightCard
            color="#1D9E75"
            title="Pre-occasion forecast"
            quote="Diwali in 18 days. Based on last year your spend will spike 40%. Plan your Diwali grocery list now — save ₹2,200 on impulse orders."
          />
        </div>
      </LayerSection>

      {/* LAYER 3 — Prescriptive ──────────────────────────────────────────── */}
      <LayerSection
        id="prescriptive"
        icon={Target}
        accent="#378ADD"
        layerLabel="Layer 3 — Prescriptive intelligence"
        title="What to do about it"
        subtitle="Dollar-value trades, not vague wellness platitudes."
        badge="Coming soon"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <InsightCard
            color="#378ADD"
            title="The Goa trip insight"
            quote="Switch Red Bull from 4 times a week to once — save ₹1,800/month. That is ₹21,600/year. One Goa trip."
          />
          <InsightCard
            color="#378ADD"
            title="Cook vs order calculator"
            quote="Cooking the same North Indian meals you order on Zomato twice a week saves ₹2,600/month — ₹31,200/year."
          />
        </div>
      </LayerSection>

      {/* LAYER 4 — Waste ─────────────────────────────────────────────────── */}
      <LayerSection
        id="waste"
        icon={Trash2}
        accent="#BA7517"
        layerLabel="Layer 4 — Waste intelligence"
        title="Where it&rsquo;s slipping through"
        subtitle="The quiet ₹10,000 a year most urban households never see."
        badge="Coming soon"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <InsightCard
            color="#BA7517"
            title="Perishable wastage signal"
            quote="You buy 2kg tomatoes weekly but reorder before they are used. Estimated waste: ₹380/month — ₹4,560/year."
          />
          <InsightCard
            color="#BA7517"
            title="Over-buying pattern"
            quote="You consistently over-buy leafy vegetables, bread, and yoghurt. Total estimated monthly waste across perishables: ₹920."
          />
        </div>
      </LayerSection>

      {/* LAYER 5 — Annual report ─────────────────────────────────────────── */}
      <section
        className="mt-12 md:mt-16 border-y border-[#1A1A28]"
        style={{ background: "#0A0A0F" }}
        data-testid="intel-layer-annual"
      >
        <div className="max-w-5xl mx-auto px-5 md:px-8 py-12 md:py-16">
          <div className="flex items-center gap-2 flex-wrap mb-3">
            <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#FF6B00]">
              Layer 5 — Annual food life report
            </span>
            <span
              className="inline-flex items-center px-2 py-0.5 text-[9px] uppercase tracking-[0.14em] font-bold"
              style={{ background: "#E24B4A", color: "#FFFFFF" }}
            >
              Highest virality
            </span>
          </div>
          <h2
            className="font-display uppercase text-3xl md:text-5xl tracking-tight leading-[1.05]"
            data-testid="intel-annual-heading"
          >
            Your 2025 Food Life —
            <br />
            <span className="text-[#FF6B00]">Annual Report.</span>
          </h2>
          <p className="mt-3 text-[#888] max-w-2xl text-sm md:text-base">
            Like Spotify Wrapped — but for everything you eat. Every January 1st.
          </p>

          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { v: "₹2,18,400", sub: "Total food spend in 2025", color: "#F0EDE8" },
              { v: "312", sub: "Orders placed — almost daily", color: "#F0EDE8" },
              { v: "68→82", sub: "Nutrition score — improved", color: "#1D9E75" },
              { v: "₹28,000", sub: "Estimated savings vs no plan", color: "#F0EDE8" },
            ].map((s, i) => (
              <div
                key={i}
                className="border border-[#2A2A35] p-4 md:p-5"
                style={{ background: "#161620" }}
                data-testid={`intel-annual-stat-${i}`}
              >
                <div
                  className="font-display font-mono-num text-2xl md:text-3xl leading-none"
                  style={{ color: s.color }}
                >
                  {s.v}
                </div>
                <div className="mt-2 text-[11px] md:text-[12px] text-[#888] leading-snug">
                  {s.sub}
                </div>
              </div>
            ))}
          </div>

          {/* Big CTA */}
          <div className="mt-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-t border-[#1A1A28] pt-8">
            <div>
              <div className="flex items-center gap-2 text-[#FF6B00] mb-1.5">
                <Calendar className="w-4 h-4" />
                <span className="text-[10px] uppercase tracking-[0.22em] font-bold">
                  Be first in line
                </span>
              </div>
              <div className="font-display uppercase text-xl md:text-2xl tracking-tight">
                Join the Super Intelligence waitlist
              </div>
              <div className="text-sm text-[#888] mt-1">
                We&rsquo;ll ping you the moment any of these layers go live.
              </div>
            </div>
            <button
              type="button"
              onClick={() => setWaitlistOpen(true)}
              className="btn-saffron px-6 py-3 inline-flex items-center justify-center gap-2 h-12"
              data-testid="intel-waitlist-cta"
            >
              <Flame className="w-4 h-4" />
              Join the Super Intelligence waitlist
            </button>
          </div>
        </div>
      </section>

      <WaitlistModal open={waitlistOpen} onClose={() => setWaitlistOpen(false)} />

      <LandingBottomNav />

      {/* Super Brain — floating CartIQ Ask AI assistant (Claude-powered).
          Same component mounted on Dashboard; signed-in users can ask
          questions about their own report data from this tab too. */}
      <CartIQAsk />
    </div>
  );
}
